/**
 * Hook for Request Call Setup modal: existing-request check, form state, submit to Firestore + API.
 */

import { useState, useEffect } from 'react';
import { collection, addDoc, serverTimestamp, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { API } from '../config/api';
const SESSION_KEY_WEBSITE_OWNER = 'callRequestSubmitted_website_owner';
const SESSION_KEY_SALES_PARTNER = 'callRequestSubmitted_sales_partner';
const LOADER_MIN_DISPLAY_MS = 2000;

function getSessionKey(isSalesPartnerRequest) {
  return isSalesPartnerRequest ? SESSION_KEY_SALES_PARTNER : SESSION_KEY_WEBSITE_OWNER;
}

const getInitialFormData = (user, userData, packageData, isSalesPartnerRequest) => ({
  name: user?.displayName || userData?.name || '',
  email: user?.email || '',
  mobile: '',
  alternateMobile: '',
  interestedPackage: packageData?.name || (isSalesPartnerRequest ? 'Sales Partner' : '') || ''
});

export function useRequestCallSetup({ isOpen, user, userData, packageData, isSalesPartnerRequest = false }) {
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [hasExistingRequestInDb, setHasExistingRequestInDb] = useState(false);
  const [loadingRequestCheck, setLoadingRequestCheck] = useState(false);
  const [formData, setFormData] = useState(() => getInitialFormData(user, userData, null, isSalesPartnerRequest));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // When modal opens, sync form from context: name, email, and default package from the button they clicked
  useEffect(() => {
    if (!isOpen) return;
    const name = user?.displayName || userData?.name || '';
    const email = user?.email || '';
    const pkg = (packageData?.name || (isSalesPartnerRequest ? 'Sales Partner' : '') || '').trim();
    setFormData((prev) => ({
      ...prev,
      ...(name && { name }),
      ...(email && { email }),
      interestedPackage: pkg || prev.interestedPackage || ''
    }));
  }, [isOpen, user?.displayName, user?.email, userData?.name, packageData?.name, isSalesPartnerRequest]);

  useEffect(() => {
    if (!isOpen) return;
    const key = getSessionKey(isSalesPartnerRequest);
    setAlreadySubmitted(typeof sessionStorage !== 'undefined' && sessionStorage.getItem(key) === 'true');
  }, [isOpen, isSalesPartnerRequest]);

  useEffect(() => {
    const rawEmail = (formData.email || user?.email || '').trim();
    if (!isOpen || !rawEmail) {
      if (!isOpen) {
        setLoadingRequestCheck(false);
        setHasExistingRequestInDb(false);
      }
      return;
    }
    setLoadingRequestCheck(true);
    const normalizedEmail = rawEmail.toLowerCase();
    let cancelled = false;
    const start = Date.now();
    const checkExisting = async () => {
      try {
        const qNorm = query(
          collection(db, 'callSetupRequests'),
          where('email', '==', normalizedEmail),
          limit(10)
        );
        const snapNorm = await getDocs(qNorm);
        if (cancelled) return;
        const hasExistingForThisType = snapNorm.docs.some((d) => {
          const type = d.data().requestType;
          if (isSalesPartnerRequest) return type === 'sales_partner';
          return type !== 'sales_partner';
        });
        setHasExistingRequestInDb(hasExistingForThisType);
        if (!hasExistingForThisType && rawEmail !== normalizedEmail) {
          const qRaw = query(
            collection(db, 'callSetupRequests'),
            where('email', '==', rawEmail),
            limit(10)
          );
          const snapRaw = await getDocs(qRaw);
          if (cancelled) return;
          const hasExistingRaw = snapRaw.docs.some((d) => {
            const type = d.data().requestType;
            if (isSalesPartnerRequest) return type === 'sales_partner';
            return type !== 'sales_partner';
          });
          setHasExistingRequestInDb(hasExistingRaw);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error checking existing request:', err);
          setHasExistingRequestInDb(false);
        }
      } finally {
        const elapsed = Date.now() - start;
        const remaining = Math.max(0, LOADER_MIN_DISPLAY_MS - elapsed);
        await new Promise((r) => setTimeout(r, remaining));
        if (!cancelled) setLoadingRequestCheck(false);
      }
    };
    checkExisting();
    return () => {
      cancelled = true;
    };
  }, [isOpen, formData.email, user?.email, isSalesPartnerRequest]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (alreadySubmitted || hasExistingRequestInDb) return;
    if (!user) {
      setErrorMessage('Please sign in with Google to submit a request.');
      setShowErrorModal(true);
      return;
    }
    const normalizedEmail = (formData.email || '').trim().toLowerCase();
    if (!normalizedEmail) {
      setErrorMessage('Please provide a valid email address.');
      setShowErrorModal(true);
      return;
    }
    const mobile = (formData.mobile || '').trim();
    const alternateMobile = (formData.alternateMobile || '').trim();
    if (alternateMobile && mobile && alternateMobile === mobile) {
      setErrorMessage('Alternate mobile number must be different from mobile number.');
      setShowErrorModal(true);
      return;
    }
    try {
      const existingQ = query(
        collection(db, 'callSetupRequests'),
        where('email', '==', normalizedEmail),
        limit(10)
      );
      const existingSnap = await getDocs(existingQ);
      const hasDuplicateForThisType = existingSnap.docs.some((d) => {
        const type = d.data().requestType;
        if (isSalesPartnerRequest) return type === 'sales_partner';
        return type !== 'sales_partner';
      });
      if (hasDuplicateForThisType) {
        setErrorMessage(
          'A call setup request has already been submitted for this email address. We will contact you shortly.'
        );
        setShowErrorModal(true);
        return;
      }
      const rawEmail = (formData.email || '').trim();
      if (rawEmail !== normalizedEmail) {
        const existingRawQ = query(
          collection(db, 'callSetupRequests'),
          where('email', '==', rawEmail),
          limit(10)
        );
        const existingRawSnap = await getDocs(existingRawQ);
        const hasDuplicateRaw = existingRawSnap.docs.some((d) => {
          const type = d.data().requestType;
          if (isSalesPartnerRequest) return type === 'sales_partner';
          return type !== 'sales_partner';
        });
        if (hasDuplicateRaw) {
          setErrorMessage(
            'A call setup request has already been submitted for this email address. We will contact you shortly.'
          );
          setShowErrorModal(true);
          return;
        }
      }
    } catch (err) {
      console.error('Error checking duplicate email:', err);
      setErrorMessage('Unable to verify request. Please try again.');
      setShowErrorModal(true);
      return;
    }
    setIsSubmitting(true);
    try {
      const requestData = {
        name: formData.name,
        email: normalizedEmail,
        mobile: formData.mobile,
        alternateMobile: formData.alternateMobile || null,
        interestedPackage: (formData.interestedPackage || packageData?.name || '').trim() || 'Not specified',
        userId: user?.uid || null,
        status: 'pending',
        requestType: isSalesPartnerRequest ? 'sales_partner' : 'website_owner',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      await addDoc(collection(db, 'callSetupRequests'), requestData);
      try {
        const response = await fetch(API.sendCallRequest, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...requestData,
            recipients: [
              'amit.mca.pu@gmail.com',
              'lkothari@gmail.com',
              'unscriptedmayank@gmail.com',
              'arnabbhowmick.2014@gmail.com'
            ]
          })
        });
        if (!response.ok) console.warn('Email sending failed, but request saved to database');
      } catch (emailError) {
        console.warn('Email sending error:', emailError);
      }
      sessionStorage.setItem(getSessionKey(isSalesPartnerRequest), 'true');
      setAlreadySubmitted(true);
      setShowSuccessModal(true);
      setFormData(getInitialFormData(user, userData, packageData, isSalesPartnerRequest));
    } catch (error) {
      console.error('Error submitting request:', error);
      let errorMsg = 'Failed to submit request. Please try again.';
      if (error.code === 'permission-denied' || error.message?.includes('permission')) {
        errorMsg = 'Permission denied. Please make sure you are signed in and try again.';
      } else if (error.code === 'unavailable' || error.message?.includes('network')) {
        errorMsg = 'Network error. Please check your connection and try again.';
      } else if (error.message) {
        errorMsg = error.message;
      }
      setErrorMessage(errorMsg);
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    setFormData,
    handleChange,
    handleSubmit,
    alreadySubmitted,
    hasExistingRequestInDb,
    loadingRequestCheck,
    isSubmitting,
    showSuccessModal,
    setShowSuccessModal,
    showErrorModal,
    setShowErrorModal,
    errorMessage,
    setErrorMessage
  };
}
